import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Share2, Copy, ExternalLink, Loader2, Trash2, Check } from "lucide-react";

const OPTIONS = [["6", "Last 3 exchanges"], ["12", "Last 6 exchanges"], ["all", "Whole conversation"]];

export function CoachShareDialog({ open, onClose, messageCount }) {
  const [count, setCount] = useState("12");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [past, setPast] = useState([]);
  useEffect(() => { if (open) { setLink(null); api.get("/coach/shares").then((r) => setPast(r.data.items)).catch(() => {}); } }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/coach/share", { count: count === "all" ? null : Number(count) });
      const url = `${window.location.origin}${r.data.path}`;
      setLink(url);
      setPast((p) => [{ id: r.data.share_id, created_at: new Date().toISOString(), count: r.data.count, preview: "" }, ...p]);
      if (navigator.share) { try { await navigator.share({ title: "My coaching session with Linden", url }); } catch { /* user cancelled */ } }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create a share link"); }
    finally { setBusy(false); }
  };
  const copy = async (url) => { await navigator.clipboard.writeText(url); toast.success("Link copied"); };
  const remove = async (id) => { await api.delete(`/coach/share/${id}`); setPast((p) => p.filter((x) => x.id !== id)); if (link?.endsWith(id)) setLink(null); toast.success("Link removed"); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="coach-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-emerald-600" /> Share this coaching session</DialogTitle>
          <DialogDescription>Creates a read-only snapshot anyone with the link can read. Your name shows as first name only. Remove it any time.</DialogDescription>
        </DialogHeader>
        {!link ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2" data-testid="coach-share-options">
              {OPTIONS.map(([v, l]) => (
                <button key={v} onClick={() => setCount(v)} disabled={v !== "all" && Number(v) > messageCount && messageCount >= 2}
                  className={`rounded-xl border px-2 py-2.5 text-xs font-semibold transition-colors disabled:opacity-40 ${count === v ? "border-emerald-500 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-200" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid={`coach-share-opt-${v}`}>{l}</button>
              ))}
            </div>
            <Button onClick={create} disabled={busy || messageCount < 2} className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold h-11" data-testid="coach-share-create">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4 mr-2" /> Create share link</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="coach-share-result">
            <div className="flex items-center gap-2 text-emerald-700 text-sm font-semibold"><Check className="w-4 h-4" /> Link ready</div>
            <div className="flex gap-2">
              <input readOnly value={link} className="flex-1 min-w-0 h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-mono text-slate-700" data-testid="coach-share-link" />
              <Button size="sm" onClick={() => copy(link)} className="h-10 rounded-xl bg-slate-900 hover:bg-slate-800 text-white" data-testid="coach-share-copy"><Copy className="w-4 h-4" /></Button>
              <a href={link} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-10 rounded-xl" data-testid="coach-share-open"><ExternalLink className="w-4 h-4" /></Button></a>
            </div>
            <button onClick={() => setLink(null)} className="text-xs text-slate-500 hover:underline">Create another</button>
          </div>
        )}
        {past.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Your shared sessions</div>
            <ul className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="coach-share-list">
              {past.map((p) => (
                <li key={p.id} className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500 shrink-0">{new Date(p.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                  <span className="flex-1 min-w-0 truncate text-slate-700">{p.preview || `${p.count} messages`}</span>
                  <button onClick={() => copy(`${window.location.origin}/coach-share/${p.id}`)} className="text-slate-500 hover:text-slate-900" title="Copy link"><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-600" title="Remove link" data-testid={`coach-share-remove-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
